/**
 * Prayer Times Card Component
 *
 * Shows today's prayer times for the mosque
 */

import { FC } from 'react';
import { ClockIcon } from '@heroicons/react/24/outline';
import { usePrayerTimes } from '@/hooks/usePrayerTimes';
import { useCompany } from '@/hooks/useCompany';

export const PrayerTimesCard: FC = () => {
  const { company } = useCompany();

  const {
    data: prayerData,
    isLoading,
    error,
    refetch,
  } = usePrayerTimes({
    companyId: company?.companyId,
    refreshInterval: 600000, // Refresh every 10 minutes
  });

  if (isLoading && !prayerData) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/2 mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-100 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Prayer Times</h3>
        <div className="text-center py-4">
          <div className="mb-4">
            <svg
              className="mx-auto h-12 w-12 text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <p className="text-red-600 text-sm font-medium">Failed to load prayer times</p>
          <p className="text-gray-500 text-xs mt-1">{error.message}</p>
          <button
            onClick={refetch}
            disabled={isLoading}
            className="mt-4 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? 'Retrying...' : 'Retry'}
          </button>
        </div>
      </div>
    );
  }

  if (!prayerData) {
    return null;
  }

  const nextPrayer = prayerData.prayers.find((prayer) => prayer.is_next);

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Prayer Times</h3>
        <p className="text-sm text-gray-600">{prayerData.date}</p>
        {prayerData.hijri_date && <p className="text-xs text-gray-500">{prayerData.hijri_date}</p>}
      </div>

      {nextPrayer && (
        <div className="mb-4 p-3 bg-primary-50 rounded-lg border border-primary-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-primary-600 font-medium uppercase">Next Prayer</p>
              <p className="text-lg font-bold text-primary-900">{nextPrayer.name}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Adhan</p>
              <p className="text-xl font-bold text-primary-600">{nextPrayer.adhan_time}</p>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {prayerData.prayers.map((prayer) => (
          <div
            key={prayer.name}
            className={`flex items-center justify-between py-2 px-3 rounded ${
              prayer.is_past ? 'bg-gray-50 opacity-60' : 'bg-white'
            } ${prayer.is_next ? 'ring-2 ring-primary-200' : ''}`}
          >
            <div className="flex items-center">
              <ClockIcon className="h-4 w-4 text-gray-400 mr-2" />
              <span className="font-medium text-gray-900">{prayer.name}</span>
            </div>
            <div className="flex gap-4 text-sm">
              <div className="text-right">
                <p className="text-xs text-gray-500">Adhan</p>
                <p className="font-semibold text-gray-900">{prayer.adhan_time}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">Iqamah</p>
                <p className="font-semibold text-gray-900">{prayer.iqamah_time}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {prayerData.sunrise && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <div className="flex justify-between items-center text-sm">
            <span className="text-gray-600">Sunrise</span>
            <span className="font-semibold text-gray-900">{prayerData.sunrise}</span>
          </div>
        </div>
      )}

      {prayerData.jumuah && prayerData.jumuah.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <p className="text-xs text-gray-500 font-medium mb-2">Jumuah</p>
          <div className="space-y-1">
            {prayerData.jumuah.map((jumuah) => (
              <div key={jumuah.name} className="flex justify-between items-center text-sm">
                <span className="text-gray-600">{jumuah.name}</span>
                <span className="font-semibold text-gray-900">{jumuah.time}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-gray-200">
        <p className="text-xs text-gray-500 text-center">Prayer times from AthanPlus</p>
        {prayerData.next_day_changes && (
          <p className="text-xs text-gray-500 text-center mt-1">{prayerData.next_day_changes}</p>
        )}
      </div>
    </div>
  );
};
